import React from 'react';
import type { ChatWidgetProps, MountAPI } from './types';

export function mount(
  options: { container?: HTMLElement; props?: ChatWidgetProps } = {}
): MountAPI {
  const { container, props = {} } = options;
  const target = container || document.createElement('div');
  const created = !container;
  if (created) document.body.appendChild(target);

  // Lazy-require to support UMD script-tag usage
  const ReactDOM = require('react-dom');
  let root: { render: (el: React.ReactElement) => void; unmount: () => void } | null = null;

  try {
    const ReactDOMClient = require('react-dom/client');
    if (ReactDOMClient?.createRoot) {
      root = ReactDOMClient.createRoot(target);
    }
  } catch {
    // Fall back to legacy render
  }

  // Lazy import ChatWidget to avoid circular dependency at module level
  const { default: ChatWidget } = require('./ChatWidget');

  const noop = () => {};
  const rejectNotReady = () => Promise.reject(new Error('Widget not mounted yet'));
  const api: MountAPI = {
    open: noop,
    close: noop,
    toggle: noop,
    unmount: noop,
    createThread: rejectNotReady as MountAPI['createThread'],
    updateThread: rejectNotReady as MountAPI['updateThread'],
    deleteThread: rejectNotReady as MountAPI['deleteThread'],
    createMessage: rejectNotReady as MountAPI['createMessage'],
    fetchThread: rejectNotReady as MountAPI['fetchThread'],
  };

  type WrapperProps = ChatWidgetProps & { defaultOpen?: boolean; open?: boolean };

  const Wrapper: React.FC<WrapperProps> = p => {
    const [open, setOpen] = React.useState(!!p.defaultOpen || !!p.open);
    const ref = React.useRef<{
      createThread: MountAPI['createThread'];
      updateThread: MountAPI['updateThread'];
      deleteThread: MountAPI['deleteThread'];
      createMessage: MountAPI['createMessage'];
      fetchThread: MountAPI['fetchThread'];
    }>(null);

    api.open = () => setOpen(true);
    api.close = () => setOpen(false);
    api.toggle = () => setOpen(v => !v);
    api.createThread = args =>
      ref.current
        ? ref.current.createThread(args)
        : Promise.reject(new Error('Widget not mounted'));
    api.updateThread = (id, patch) =>
      ref.current
        ? ref.current.updateThread(id, patch)
        : Promise.reject(new Error('Widget not mounted'));
    api.deleteThread = id =>
      ref.current ? ref.current.deleteThread(id) : Promise.reject(new Error('Widget not mounted'));
    api.createMessage = args =>
      ref.current
        ? ref.current.createMessage(args)
        : Promise.reject(new Error('Widget not mounted'));
    api.fetchThread = () =>
      ref.current ? ref.current.fetchThread() : Promise.reject(new Error('Widget not mounted'));

    return React.createElement(ChatWidget, { ...p, open, onOpenChange: setOpen, ref });
  };

  const element = React.createElement(Wrapper, props);
  if (root) {
    root.render(element);
    api.unmount = () => {
      root!.unmount();
      if (created && target.parentNode) target.parentNode.removeChild(target);
    };
  } else {
    // Legacy React <18 fallback for broad compat
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(element, target);
    api.unmount = () => {
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.unmountComponentAtNode(target);
      if (created && target.parentNode) target.parentNode.removeChild(target);
    };
  }

  return api;
}
